'use client';

import { IoAppsOutline } from 'react-icons/io5';

import { IconButton } from '../../../../icon-button';
import { ResourceBody } from '../../../../resource';
import type { InstructionsType } from '../../../Question.types';
import { PagerBar } from '../../../layout/PagerBar';
import { Question } from '../../../layout/Question';
import { FlagButton, PauseButton } from '../../../layout/QuestionControls';
import { QuestionToolbar } from '../../../layout/QuestionToolbar';
import type { MultipleChoiceQuestionType } from '../../../multiple-choice-question/MultipleChoiceQuestion.types';

type CloseAction = 'exit' | 'pause-and-exit' | 'none';

type QuestionStepProps = {
  question?: MultipleChoiceQuestionType;
  index: number;
  total: number;
  /** Read-only: a manual sitting is answered on paper, not on screen. */
  readOnly: boolean;
  secondsLeft?: number;
  instructions?: InstructionsType;
  closeAction: CloseAction;
  pauseDisabled: boolean;
  onResponse: (optionIndex: number) => void;
  onBookmark?: () => void;
  onFlag: () => void;
  onPause: () => void;
  onShowIndex: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onDiscard?: () => void;
  onClose?: () => void;
};

function closeConfirmationMessage(
  closeAction: CloseAction,
  canDiscard: boolean,
) {
  if (closeAction === 'none') {
    return '¿Estás segurx de que deseas descartar el simulacro? No podrás retomarlo.';
  }

  return closeAction === 'pause-and-exit' && canDiscard
    ? '¿Quieres pausar el simulacro o descartarlo?'
    : '¿Estás segurx de que deseas abandonar el simulacro?';
}

/** The sitting itself: one question at a time, with the clock running. */
function QuestionStep({
  question,
  index,
  total,
  readOnly,
  secondsLeft,
  instructions,
  closeAction,
  pauseDisabled,
  onResponse,
  onBookmark,
  onFlag,
  onPause,
  onShowIndex,
  onPrevious,
  onNext,
  onDiscard,
  onClose,
}: QuestionStepProps) {
  return (
    <>
      <QuestionToolbar
        secondsLeft={secondsLeft}
        bookmarked={question?.bookmarked}
        onBookmark={onBookmark}
        instructions={instructions}
        options={<FlagButton flagged={question?.flagged} onFlag={onFlag} />}
        actions={<PauseButton disabled={pauseDisabled} onPause={onPause} />}
        close={
          onClose && {
            closeConfirmation: true,
            confirmationMessage: closeConfirmationMessage(
              closeAction,
              !!onDiscard,
            ),
            confirmationSubtitle:
              closeAction === 'pause-and-exit'
                ? 'Si lo pausas, podrás retomarlo más tarde desde la pantalla de simulacros en progreso'
                : undefined,
            closeAction,
            onDiscard,
            onClose,
          }
        }
      />
      <ResourceBody key={question?.id}>
        {question && (
          <Question
            question={{
              ...question,
              subject: `Pregunta ${index + 1} de ${total}`,
            }}
            readOnly={readOnly}
            onResponse={onResponse}
          />
        )}
      </ResourceBody>
      <PagerBar onPrevious={onPrevious} onNext={onNext}>
        <IconButton
          icon={<IoAppsOutline />}
          aria-label="Ver índice de preguntas"
          onClick={onShowIndex}
        />
      </PagerBar>
    </>
  );
}

export { QuestionStep };
export type { CloseAction };
